import { Form } from "@unform/web";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";

import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";

import ProjectConversationMessages from "../ConversationMessages";
import ConversationModal from "../ConversationModal";
import * as S from "./styles";

const ProjectConversations = ({ projectId, onModerated }) => {
  const { api } = UseServices();

  const [adminConversations, setAdminConversations] = useState([]);
  const [otherConversations, setOtherConversations] = useState([]);

  const [selectedConversation, setSelectedConversation] = useState(null);

  const [openModal, setOpenModal] = useState(false);

  /*
   * ⚠️ GUARDA DO `projectId` (24/09/2026). A sub-aba é renderizada no primeiro
   * render do detalhe, quando `selectedProject` ainda é `{}` — logo `projectId`
   * chega `undefined`. Sem a guarda, a busca saía com `project_id: undefined`, o
   * backend recusava ("ID do projeto não informado") e, com deps `[]`, NUNCA
   * havia segunda tentativa: a sessão ficava vazia para sempre.
   *
   * Pelo caminho normal ninguém via, porque a aba padrão é "Documentos" e o
   * projeto já carregou quando o admin clica aqui. Quem expõe é o DEEP LINK
   * `?tab=` — que é justamente o que o e-mail ao admin e a tela de pendências
   * usam.
   */
  useEffect(() => {
    if (projectId) getProjectConversations(projectId);

    // eslint-disable-next-line
  }, [projectId]);

  const getProjectConversations = (project_id) => {
    api
      .post("/projects/conversations", {
        project_id: project_id,
      })
      .then((response) => {
        const admin_conversations = response.data?.admin_conversations;
        const other_conversations = response.data?.other_conversations;

        setAdminConversations(admin_conversations);
        setOtherConversations(other_conversations);
      })
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  const listConversation = (title, conversations, isOther = false) => {
    return conversations.length ? (
      <>
        <S.Section>
          <span>{title}</span>
          <hr />
        </S.Section>

        <S.Conversations>
          {conversations.map((conversation) => (
            <S.Conversation
              key={conversation.id}
              onClick={() =>
                setSelectedConversation({
                  ...conversation,
                  is_my_conversation: !isOther,
                })
              }
            >
              {/* Ausência, não zero: "0" na tela é ruído, e é a convenção que
                  os botões das sessões já usam. */}
              {conversation.pending_messages ? (
                <S.ConversationBadge
                  title={`${conversation.pending_messages} mensagem(ns) aguardando sua avaliação`}
                >
                  {conversation.pending_messages}
                </S.ConversationBadge>
              ) : null}

              <S.ConversationInfos>
                <h2>{conversation?.users[1]?.full_name}</h2>
                {isOther && <h3>{conversation?.users[0]?.full_name}</h3>}

                {/* "Não lidas" só em "Suas Conversas" (24/09/2026).
                    Em "Outras Conversas" o admin é OBSERVADOR: `unread_messages`
                    conta as não lidas de um dos dois participantes, escolhido
                    arbitrariamente por `find(id => id !== user_id)` — e ali os
                    DOIS são diferentes dele. O número era exibido e estava
                    errado; quem responde "há algo para eu fazer aqui?" é o
                    badge. */}
                {!isOther && conversation.unread_messages ? (
                  <span>
                    <strong>{conversation.unread_messages}</strong> mensagens
                    não lidas
                  </span>
                ) : null}
              </S.ConversationInfos>
            </S.Conversation>
          ))}
        </S.Conversations>
      </>
    ) : null;
  };

  /*
   * Moderar dentro de uma conversa precisa derrubar DOIS badges: o do card
   * (lista local) e o do botão da sessão (estado do pai).
   *
   * A lista local não se atualiza sozinha: voltar da conversa só faz
   * `setSelectedConversation(null)`, que re-renderiza o componente JÁ montado —
   * o efeito com deps `[projectId]` não roda de novo, e o badge ficaria lá
   * anunciando algo que o admin acabou de resolver.
   */
  const handleModerated = () => {
    getProjectConversations(projectId);

    if (onModerated) onModerated();
  };

  return selectedConversation ? (
    <ProjectConversationMessages
      projectId={projectId}
      conversation={selectedConversation}
      setConversation={setSelectedConversation}
      onModerated={handleModerated}
    />
  ) : (
    <S.Wrapper>
      {openModal ? (
        <ConversationModal
          projectId={projectId}
          setOpenModal={setOpenModal}
          getProjectConversations={getProjectConversations}
        />
      ) : null}
      <Form>
        {/*
          Botão SEMPRE visível (05/09/2026). Antes ele vivia dentro do bloco
          "Suas Conversas" e tinha um duplicado no caso de lista vazia — o que
          deixava um buraco: com apenas "Outras Conversas" preenchida, nenhum
          dos dois renderizava e o admin não conseguia abrir conversa nenhuma.
          Iniciar conversa é ação da SESSÃO, não de uma das listas.
        */}
        <S.Section>
          <span>Conversas</span>
          <hr />
        </S.Section>

        <S.Buttons>
          <div />

          <Button
            type="button"
            text="Criar Conversa"
            onClick={() => setOpenModal(true)}
          />
        </S.Buttons>

        {listConversation("Suas Conversas", adminConversations)}
        {listConversation("Outras Conversas", otherConversations, true)}

        {!adminConversations.length && !otherConversations.length ? (
          <span>Nenhuma conversa neste projeto.</span>
        ) : null}
      </Form>
    </S.Wrapper>
  );
};

export default ProjectConversations;
